import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";

import { Button } from "../Button";
import { TextField } from "../TextField";
import { Select } from "../Select";
import { CardFooter } from "../Card/Card";
import type { UtilityInfoState } from "./types";
import type { RateScheduleOption } from "./types";
import type { SplatUtilityResponse } from "./types";
import { UtilityModal } from "./UtilityModal";

const UtilityInfoCardEdit = ({
  initialUtilityData,
  onUtilityInfoSave,
  onCancel,
  utilityCompanies,
  hasActiveProposal,
}: UtilityInfoCardEditProps) => {
  const [editUtilityCompany, setEditUtilityCompany] = useState(
    initialUtilityData.utilityCompany
  );
  const [editRateSchedule, setEditRateSchedule] = useState(
    initialUtilityData.rateSchedule
  );
  const [editAccountNumber, setEditAccountNumber] = useState(
    initialUtilityData.accountNumber
  );
  const [editMeterNumber, setEditMeterNumber] = useState(
    initialUtilityData.meterNumber
  );
  const [editBillDate, setEditBillDate] = useState(
    initialUtilityData.utilityBillDate
  );
  const [editServiceAddress, setEditServiceAddress] = useState(
    initialUtilityData.utilityServiceAddress
  );
  const [rateScheduleOptions, setRateScheduleOptions] = useState<
    RateScheduleOption[]
  >([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const getRateSchedulesFromUtilityOptions = useCallback(
    (utilityName: string | undefined) => {
      if (!utilityName) {
        return;
      }

      const utilityObj = utilityCompanies.find((utility) => {
        return utility.utilityName === utilityName;
      });

      if (utilityObj) {
        setRateScheduleOptions(utilityObj.rateSchedules);
      }
    },
    [utilityCompanies]
  );

  useMemo(() => {
    const rateOptions = (value, { name, type }) => {
      if (name === "utilityCompany" && type === "change") {
        getRateSchedulesFromUtilityOptions(value.utilityCompany ?? "");
        setEditRateSchedule("rateSchedule");
      }
    };

    return rateOptions;
  }, [getRateSchedulesFromUtilityOptions]);

  useEffect(() => {
    getRateSchedulesFromUtilityOptions(editUtilityCompany);
  }, [getRateSchedulesFromUtilityOptions, editUtilityCompany]);

  const handleSave = useCallback(async () => {
    const updateUtilityInfo = {
      utilityCompany: editUtilityCompany,
      rateSchedule: editRateSchedule,
      meterNumber: editMeterNumber,
      utilityServiceAddress: editServiceAddress,
      utilityBillDate: editBillDate,
      accountNumber: editAccountNumber,
    } as UtilityInfoState;

    const newUtility = await onUtilityInfoSave(updateUtilityInfo);

    return newUtility;
  }, [
    editAccountNumber,
    editBillDate,
    editMeterNumber,
    editRateSchedule,
    editServiceAddress,
    editUtilityCompany,
    onUtilityInfoSave,
  ]);

  const onUpdateUtilityInfo = useCallback(() => {
    if (
      hasActiveProposal &&
      (editUtilityCompany !== initialUtilityData.utilityCompany ||
        editRateSchedule !== initialUtilityData.rateSchedule)
    ) {
      setShowConfirmationModal(true);
    } else {
      handleSave();
    }
  }, [
    hasActiveProposal,
    editRateSchedule,
    editUtilityCompany,
    handleSave,
    initialUtilityData.rateSchedule,
    initialUtilityData.utilityCompany,
  ]);

  return (
    <>
      <UtilityInfoContainer>
        <div>
          <StyledEditHeader>Utility company</StyledEditHeader>
          <StyledSelect
            data-testid="utilities-info-edit-utility-company"
            value={editUtilityCompany}
            options={[
              { label: "", value: "", disabled: true },
              ...utilityCompanies.map((company: SplatUtilityResponse) => ({
                label: company.utilityName,
                value: company.utilityName,
              })),
            ]}
            onChange={(event) => setEditUtilityCompany(event.target.value)}
          />
        </div>
        <div>
          <StyledEditHeader>Rate schedule</StyledEditHeader>
          <StyledSelect
            data-testid="utilities-info-edit-rate-schedule"
            value={editRateSchedule}
            options={[
              { label: "", value: "", disabled: true },
              ...rateScheduleOptions.map(
                (rateSchedule: RateScheduleOption) => ({
                  label: rateSchedule.label,
                  value: rateSchedule.value,
                })
              ),
            ]}
            disabled={!rateScheduleOptions.length}
            onChange={(event) => setEditRateSchedule(event.target.value)}
          />
        </div>
        <TextField
          label="Account number"
          data-testid="utilities-info-edit-account-number"
          value={editAccountNumber}
          onChange={(event) => setEditAccountNumber(event.target.value)}
        />
        <TextField
          label="Meter number"
          data-testid="utilities-info-edit-meter-number"
          value={editMeterNumber}
          onChange={(event) => setEditMeterNumber(event.target.value)}
        />
        <TextField
          label="Utility service address"
          data-testid="utilities-info-edit-utility-service-address"
          helperText="Must exactly match address on utility bill"
          value={editServiceAddress}
          onChange={(event) => setEditServiceAddress(event.target.value)}
        />
        <TextField
          label="Date of latest utility bill"
          data-testid="utilities-info-edit-date-utility-bill"
          helperText="Date of the most recent utility bill uploaded"
          type="date"
          value={editBillDate}
          onChange={(event) => setEditBillDate(event.target.value)}
        />
        <StyledSectionFooterButtons
          rightContent={
            <>
              <Button color="secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                data-testid="utility-info-save"
                onClick={() => onUpdateUtilityInfo()}
              >
                Update Utility Information
              </Button>
            </>
          }
        />
      </UtilityInfoContainer>
      {showConfirmationModal && (
        <UtilityModal
          onClose={() => {
            setShowConfirmationModal(false);
          }}
          onConfirm={() => {
            handleSave();
          }}
        />
      )}
    </>
  );
};

interface UtilityInfoCardEditProps {
  initialUtilityData: UtilityInfoState;
  onCancel: () => void;
  onUtilityInfoSave: (selectedUtility: UtilityInfoState) => void;
  utilityCompanies: SplatUtilityResponse[];
  hasActiveProposal: boolean;
}

const StyledEditHeader = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: gray;
  margin-top: 24px;
  margin-bottom: 8px;
`;

const StyledSectionFooterButtons = styled(CardFooter)`
  grid-column-start: 2;
`;

const UtilityInfoContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
`;

const StyledSelect = styled(Select)`
  > button {
    padding: 11px 18px;
    min-height: 48px;
    > .chevron {
      margin-left: auto;
    }
  }
`;

export { UtilityInfoCardEdit };
